import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Toaster } from "@/components/Toaster";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageLoader } from "@/components/layout/PageLoader";
import { canViewModule } from "@/lib/permissions";
import { queryClient } from "@/lib/queryClient";
import { useAuthStore } from "@/stores/auth.store";
import { QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

// Lazy-loaded pages for Code-Splitting
const LoginPage = lazy(() => import("@/pages/LoginPage").then((m) => ({ default: m.LoginPage })));
const TasksPage = lazy(() => import("@/pages/TasksPage").then((m) => ({ default: m.TasksPage })));
const CalendarPage = lazy(() =>
  import("@/pages/CalendarPage").then((m) => ({ default: m.CalendarPage })),
);
const TeamPage = lazy(() => import("@/pages/TeamPage").then((m) => ({ default: m.TeamPage })));
const MonitoringPage = lazy(() =>
  import("@/pages/MonitoringPage").then((m) => ({ default: m.MonitoringPage })),
);
const TicketsPage = lazy(() =>
  import("@/pages/TicketsPage").then((m) => ({ default: m.TicketsPage })),
);
const DemosPage = lazy(() => import("@/pages/DemosPage").then((m) => ({ default: m.DemosPage })));
const FilesPage = lazy(() => import("@/pages/FilesPage").then((m) => ({ default: m.FilesPage })));
const ApksPage = lazy(() => import("@/pages/ApksPage").then((m) => ({ default: m.ApksPage })));
const FirebasePage = lazy(() =>
  import("@/pages/FirebasePage").then((m) => ({ default: m.FirebasePage })),
);
const VaultPage = lazy(() => import("@/pages/VaultPage").then((m) => ({ default: m.VaultPage })));
const IdeasPage = lazy(() => import("@/pages/IdeasPage").then((m) => ({ default: m.IdeasPage })));
const AppsPage = lazy(() => import("@/pages/AppsPage").then((m) => ({ default: m.AppsPage })));
const PushPage = lazy(() => import("@/pages/PushPage").then((m) => ({ default: m.PushPage })));
const DevToolsPage = lazy(() =>
  import("@/pages/DevToolsPage").then((m) => ({ default: m.DevToolsPage })),
);
const AuditLogPage = lazy(() =>
  import("@/pages/AuditLogPage").then((m) => ({ default: m.AuditLogPage })),
);
const GuestAccessPage = lazy(() =>
  import("@/pages/GuestAccessPage").then((m) => ({ default: m.GuestAccessPage })),
);

// Protected Route Component
interface ProtectedRouteProps {
  element: React.ReactElement;
}

function ProtectedRoute({ element }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role === "guest") {
    return <Navigate to="/guest-access" replace />;
  }

  return element;
}

interface RoleProtectedRouteProps {
  element: React.ReactElement;
  module: string;
}

function RoleProtectedRoute({ element, module }: RoleProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role === "guest") {
    return <Navigate to="/guest-access" replace />;
  }

  const role = user?.role ?? "designer";
  if (!canViewModule(role, module)) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8">
          <h2 className="text-xl font-semibold text-zinc-900 mb-2">Access Denied</h2>
          <p className="text-zinc-500">You do not have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return element;
}

export function App() {
  const { initialize } = useAuthStore();

  useEffect(() => {
    initialize();
  }, [initialize]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Toaster />
        <ConfirmDialog />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public & Guest Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/guest-access" element={<GuestAccessPage />} />

              {/* Protected Routes */}
              <Route element={<AppLayout />}>
                <Route index element={<Navigate to="/tasks" replace />} />
                <Route path="/tasks" element={<ProtectedRoute element={<TasksPage />} />} />
                <Route path="/calendar" element={<ProtectedRoute element={<CalendarPage />} />} />
                <Route
                  path="/team"
                  element={<RoleProtectedRoute element={<TeamPage />} module="Team" />}
                />
                <Route
                  path="/monitoring"
                  element={<RoleProtectedRoute element={<MonitoringPage />} module="Monitoring" />}
                />
                <Route path="/tickets" element={<ProtectedRoute element={<TicketsPage />} />} />
                <Route
                  path="/demos"
                  element={<RoleProtectedRoute element={<DemosPage />} module="Demos" />}
                />
                <Route path="/files" element={<ProtectedRoute element={<FilesPage />} />} />
                <Route
                  path="/apks"
                  element={<RoleProtectedRoute element={<ApksPage />} module="APKs" />}
                />
                <Route
                  path="/firebase"
                  element={<RoleProtectedRoute element={<FirebasePage />} module="Firebase" />}
                />
                <Route
                  path="/vault"
                  element={<RoleProtectedRoute element={<VaultPage />} module="Vault" />}
                />
                <Route path="/ideas" element={<ProtectedRoute element={<IdeasPage />} />} />
                <Route path="/apps" element={<ProtectedRoute element={<AppsPage />} />} />
                <Route path="/push" element={<ProtectedRoute element={<PushPage />} />} />
                <Route
                  path="/dev-tools"
                  element={<RoleProtectedRoute element={<DevToolsPage />} module="Dev Tools" />}
                />
                <Route
                  path="/audit-logs"
                  element={<RoleProtectedRoute element={<AuditLogPage />} module="Audit Logs" />}
                />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
